import * as React from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import type { DashboardPropertyFilters, Tag } from "../../../convex/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import type { PropertyFilterChip, PropertyListingAgentOption } from "./filters";
import { AdvancedPropertyFilters } from "./property-table-advanced-filters";

type PropertyTableFiltersProps = {
  filters: DashboardPropertyFilters;
  activeFilterCount: number;
  filterChips: PropertyFilterChip[];
  moreFiltersOpen: boolean;
  tags: Tag[];
  listingAgents: PropertyListingAgentOption[] | undefined;
  onFiltersChange: (nextFilters: DashboardPropertyFilters) => void;
  onClearFilters: () => void;
  onRemoveChip: (chipKey: string) => void;
  onMoreFiltersOpenChange: (open: boolean) => void;
  headerAction?: React.ReactNode;
};

/**
 * Compact quick bar plus advanced filter drawer for the properties table.
 */
export function PropertyTableFilters({
  filters,
  activeFilterCount,
  filterChips,
  moreFiltersOpen,
  tags,
  listingAgents,
  onFiltersChange,
  onClearFilters,
  onRemoveChip,
  onMoreFiltersOpenChange,
  headerAction,
}: PropertyTableFiltersProps) {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      searchQuery: event.target.value,
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search properties"
            value={filters.searchQuery}
            onChange={handleSearchChange}
            placeholder="Search properties…"
            className="h-9 min-h-9 pl-8 text-base transition-colors"
            spellCheck={false}
            autoComplete="off"
          />
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-1.5 sm:justify-end">
          <Sheet open={moreFiltersOpen} onOpenChange={onMoreFiltersOpenChange}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Filters</span>
                {activeFilterCount > 0 ? (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] leading-none tabular-nums text-muted-foreground">
                    {activeFilterCount}
                  </span>
                ) : null}
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full gap-0 p-0 sm:max-w-[42rem]"
            >
              <SheetHeader className="border-b bg-background px-4 py-3 pr-12">
                <SheetTitle>Advanced filters</SheetTitle>
                <SheetDescription>
                  Refine the property list by tag, listing agents, availability,
                  visibility, type, size, and price.
                </SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <AdvancedPropertyFilters
                  filters={filters}
                  tags={tags}
                  listingAgents={listingAgents}
                  onFiltersChange={onFiltersChange}
                />
              </div>
              <SheetFooter className="border-t bg-background px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  {activeFilterCount === 0
                    ? "Add a filter to narrow the list."
                    : `${activeFilterCount} filters are narrowing this list.`}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  disabled={activeFilterCount === 0}
                  onClick={onClearFilters}
                >
                  Clear all
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>

          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs active:scale-95 transition-all duration-150 ease-out cursor-pointer"
            disabled={activeFilterCount === 0}
            onClick={onClearFilters}
          >
            Clear
          </Button>

          {headerAction ? (
            <div className="flex items-center sm:pl-1">{headerAction}</div>
          ) : null}
        </div>
      </div>

      {filterChips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {filterChips.map((chip) => (
            <Badge
              key={chip.key}
              variant="outline"
              className="gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-normal leading-tight"
            >
              {chip.label}
              <button
                type="button"
                onClick={() => onRemoveChip(chip.key)}
                className="flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                aria-label={`Remove ${chip.label} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}
